import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Check, ChevronDown, X } from "lucide-react";

/**
 * Selector unificado de plataformas (Cogniseg) - sempre le a lista real de `/platforms/lookup`.
 * Usa o campo `nome` como valor (ex: "Cognietica") para manter compat com o resto do codigo.
 *
 * Modos:
 *  - multi=false (default): comporta-se como PlatformPicker (single select). value e string.
 *  - multi=true: multi-select com checkboxes. value e array de strings.
 *
 * Props:
 *  - value: string | string[]
 *  - onChange: (v: string | string[]) => void
 *  - multi: boolean
 *  - placeholder, testid, className
 *  - includeEmpty (single-only): adiciona opcao "- Generico -"
 *  - allowFreeText (single-only): permite escrever um valor que nao esta na lista
 */
export default function PlatformSelector({
  value,
  onChange,
  multi = false,
  placeholder = "- Generico -",
  testid = "platform-selector",
  className = "",
  includeEmpty = true,
  allowFreeText = false,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancel = false;
    api.get("/platforms/lookup").then(({ data }) => {
      if (!cancel) setRows((data || []).map((p) => p.nome).filter(Boolean));
    }).catch(() => { if (!cancel) setRows([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  // Click-outside para fechar o multi-select
  useEffect(() => {
    if (!multi || !open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [multi, open]);

  if (!multi) {
    const v = value || "";
    const existsInList = rows.includes(v);
    return (
      <select
        value={existsInList ? v : (allowFreeText ? "__custom__" : v)}
        onChange={(e) => onChange && onChange(e.target.value === "__custom__" ? v : e.target.value)}
        data-testid={testid}
        className={`w-full px-3 py-2 border border-tint rounded-lg text-sm bg-white ${className}`}>
        {includeEmpty && <option value="">{placeholder}</option>}
        {loading && !rows.length && <option disabled>A carregar...</option>}
        {rows.map((n) => <option key={n} value={n}>{n}</option>)}
        {allowFreeText && v && !existsInList && <option value={v}>{v} (fora da lista)</option>}
      </select>
    );
  }

  // Multi-select
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (name) => {
    const next = selected.includes(name) ? selected.filter((x) => x !== name) : [...selected, name];
    onChange && onChange(next);
  };
  const clear = (e) => {
    e.stopPropagation();
    onChange && onChange([]);
  };
  const label = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]
    : `${selected.length} plataformas`;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={`w-full px-3 py-2 border border-tint rounded-lg text-sm bg-white text-left flex items-center justify-between gap-2 ${selected.length ? "text-navy" : "text-muted2"}`}>
        <span className="truncate flex-1">{label}</span>
        {selected.length > 0 && (
          <X size={13} onClick={clear} className="text-muted2 hover:text-red-500 flex-shrink-0"
            data-testid={`${testid}-clear`} />
        )}
        <ChevronDown size={13} className={`text-muted2 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-tint rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-muted2">A carregar...</div>}
          {!loading && rows.length === 0 && <div className="px-3 py-2 text-xs text-muted2 italic">Sem plataformas disponiveis.</div>}
          {rows.map((n) => (
            <button key={n} type="button" onClick={() => toggle(n)}
              data-testid={`${testid}-opt-${n}`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-navy hover:bg-slate-50 text-left">
              <span className={`w-4 h-4 border border-tint rounded flex items-center justify-center flex-shrink-0 ${selected.includes(n) ? "bg-blue text-white border-blue" : "bg-white"}`}>
                {selected.includes(n) && <Check size={11} />}
              </span>
              <span className="flex-1">{n}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
